import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatErr } from "../api";
import { toast } from "sonner";
import { Leaf, Gift, Sparkles } from "lucide-react";
import { trackEvent } from "../lib/analytics";
import { getFirstTouch } from "../lib/acquisition";
import Seo from "../components/Seo";
import { OFFER } from "../config/offer";
import { GoogleSignInButton, OrDivider } from "../components/GoogleSignInButton";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const referralCode = (params.get("ref") || "").trim().toUpperCase();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    trackEvent("registration_started");
    if (referralCode) {
      trackEvent("referral_landing", { code: referralCode });
    }
  }, [referralCode]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      await register(name, email, password, { ...(referralCode ? { referral_code: referralCode } : {}), acquisition: getFirstTouch() });
      trackEvent("sign_up", { method: "diy", referred: !!referralCode });
      trackEvent("registration_completed", { referred: !!referralCode });
      if (referralCode) {
        trackEvent("referral_registration", { code: referralCode });
        trackEvent("referral_reward_issued", { type: "referee_signup_bonus" });
      }
      toast.success(referralCode ? "Welcome! You've got 2 free AI designs 🎁" : "Welcome! Your free AI design is ready 🎁");
      nav("/ai-designer");
    } catch (e) {
      const msg = formatErr(e);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex" data-testid="register-page">
      <Seo
        title="Create Your Free Account — Your First AI Design Is On Us"
        description="Join Backyard Buddy free and get 1 free AI design of your own backyard — plus the Material Calculator, Seasonal Planner, and Buddy Care. No credit card required."
        path="/register"
      />
      <div className="w-full md:w-1/2 flex items-center justify-center px-6 py-16 order-2 md:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-full bg-[#2D4A3E] flex items-center justify-center">
              <Leaf className="w-5 h-5 text-[#F9F6F0]" strokeWidth={1.5} />
            </div>
            <span className="font-serif text-2xl font-medium">Backyard Buddy</span>
          </Link>

          <p className="overline text-[#D47C59] mb-2">Your first design is on us</p>
          <h1 className="font-serif text-4xl font-light mb-3">See what your backyard could become.</h1>
          <p className="text-sm text-[#5C6B62] font-light mb-8 flex items-center gap-2" data-testid="free-design-perk">
            <Sparkles className="w-4 h-4 text-[#D47C59] flex-shrink-0" /> Includes {OFFER.FREE_DESIGNS_ON_SIGNUP} free AI design of your own yard — no credit card needed.
          </p>

          <GoogleSignInButton label="Sign up with Google" source="register" />
          <OrDivider />

          {referralCode && (
            <div className="bb-card p-4 mb-6 bg-[#F4ECE1] border-[#D47C59]/40 flex items-start gap-3" data-testid="referral-banner">
              <Gift className="w-5 h-5 text-[#D47C59] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-sm text-[#1A2F25]">You were invited! 🌷</p>
                <p className="text-xs text-[#5C6B62] font-light mt-0.5">
                  Sign up now and you&apos;ll get an <strong>extra free AI design</strong> on top of your welcome design — 2 designs total, thanks to your friend&apos;s referral (code: <code>{referralCode}</code>).
                </p>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <label className="overline text-[#5C6B62] mb-2 block">Name</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bb-input"
                placeholder="Linda Greenfield"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="overline text-[#5C6B62] mb-2 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bb-input"
                placeholder="you@garden.com"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="overline text-[#5C6B62] mb-2 block">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bb-input"
                placeholder="At least 6 characters"
                data-testid="register-password-input"
              />
            </div>

            {err && <p className="text-sm text-red-700 font-light">{err}</p>}

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="register-submit-button">
              {loading ? "Creating account…" : OFFER.CTA}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#5C6B62] font-light">
            Already have an account?{" "}
            <Link to="/login" className="text-[#2D4A3E] underline underline-offset-4 hover:text-[#D47C59]" data-testid="register-to-login-link">
              Sign in
            </Link>
          </p>

          <p className="mt-6 text-xs text-[#5C6B62] font-light">
            1 free AI design included · Linda&apos;s planning tools free forever · No credit card required.
          </p>
        </div>
      </div>

      <div className="hidden md:block md:w-1/2 relative order-1 md:order-2">
        <img
          src="https://images.pexels.com/photos/8839767/pexels-photo-8839767.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-bl from-[#1A2F25]/30 to-[#1A2F25]/10" />
        <div className="absolute bottom-12 right-12 text-[#F9F6F0] max-w-md text-right">
          <p className="overline text-[#D47C59] mb-3">Your dream yard awaits</p>
          <p className="font-serif text-4xl font-light leading-tight">
            "To plant a garden is to believe in tomorrow."
          </p>
          <p className="text-sm font-light mt-4 text-[#F9F6F0]/70">— Audrey Hepburn</p>
        </div>
      </div>
    </div>
  );
}
